// Utilitaire pour extraire un message d'erreur lisible depuis n'importe quelle réponse d'API.
// Corrige le bug "[object Object]" ("objet objet") quand le backend renvoie un `detail`
// structuré (dict Pydantic, tableau d'erreurs de validation, etc.).

/**
 * Extract a human-readable error message from any API error.
 * Handles: axios errors, fetch errors, Pydantic 422 arrays, structured details, plain strings.
 *
 * @param {any} err — anything (Error, axios error, fetch Response body, string, object, array)
 * @param {string} fallback — default message if nothing usable is found
 * @returns {string}
 */
export function extractErrorMessage(err, fallback = 'Une erreur est survenue') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  // Axios error → err.response.data.detail (or err.message)
  const detail = err?.response?.data?.detail ?? err?.data?.detail ?? err?.detail;
  const source = detail !== undefined ? detail : err;

  return _stringify(source, fallback, err);
}

function _stringify(v, fallback, originalErr) {
  if (v == null) {
    return originalErr?.message || fallback;
  }
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);

  // Pydantic validation error: [{loc, msg, type}, ...]
  if (Array.isArray(v)) {
    const parts = v
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          if (item.msg) return item.msg;
          if (item.message) return item.message;
        }
        return null;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' • ');
  }

  // Object detail: { code, message, ... } (e.g. our GEO_BLOCKED shape)
  if (typeof v === 'object') {
    if (v.message) return String(v.message);
    if (v.detail) return _stringify(v.detail, fallback, originalErr);
    if (v.msg) return String(v.msg);
    if (v.error) return _stringify(v.error, fallback, originalErr);
  }

  // Last resort: use Error.message if we lost the structured detail
  return originalErr?.message || fallback;
}
